import { useEffect, useState, ReactElement } from 'react';
import { useRouter } from 'next/router';
import {
  castArray, isEqual, omit,
} from 'lodash';
import { notify } from 'lib/utils/honeybadgerUtils';
import { Post } from 'lib/types';
import {
  ESPaginationInfo, PostServiceMeta, PostServiceStats,
} from 'server/api/types';
import { useApi, useApplicationState } from 'lib/contexts/ApplicationState';
import { PostSearchCriteria, FilterResult, PostSort } from 'lib/types/filters';
import {
  basicPostSortOptions, updateRouterQuery,
  queryToCriteria, queryToFilters,
} from 'lib/utils/filterUtils';
import { Pagination } from 'components/Pagination/ESPagination';
import { MultiFilter } from 'components/MultiFilters';
import { SortFilter } from 'components/Filters/SortFilter';
import { NetworkFilter } from 'components/Filters/NetworkFilter';
import { FileTypeFilter } from 'components/Filters/FileTypeFilter';
import { MoreFilter } from 'components/Filters/MoreFilter';
import { FilterPostTitle } from 'components/FilterPostTitle';
import { ShareButton } from 'components/ShareButton';
import { TimeframeOptionsFilter } from 'components/Filters/TimeframeOptionsFilter';
import { defaultTimeFrameOptions } from 'components/Filters/utils';
import { useSnackbar } from 'notistack';
import { UserListFilter } from 'components/Filters/UserListFilter';
import {
  Button,
} from '@mui/material';
import { Download, CheckCircle } from '@mui/icons-material';
import ListAlt from '@mui/icons-material/ListAlt';
import ReorderIcon from '@mui/icons-material/Reorder';
import { OnlySavedFilter } from 'components/Filters/OnlySavedFilter';
import { PostStats } from 'components/PostStats';
import {
  userIsPaidTeamMember, pluralize,
} from 'lib/helpers';
import { useTeams } from 'lib/contexts/teamsContext';
import { CreditedStatusFilter } from 'components/Filters/SelectCreditedStatusFilter';
import {
  trackClickSwitchToMyImports, trackClickSignupEvent,
} from 'lib/hooks/useEventTracking';
import { Manager } from 'features/Manager/NewManager';
import { AddDrawer, Method, DrawerType } from 'features/Manager/AddDrawer';
import { AddTags } from 'features/Manager/AddTags';
import { CreditDrawer } from 'features/Manager/CreditDrawer';
import { SelectedPosts } from 'features/Manager/SelectedPosts';
import { MatchDrawer } from 'features/Manager/MatchDrawer';
import { SimilarPosts } from 'features/Manager/SimilarPosts';
import { Credit } from 'lib/types/credits';
import { Tag } from 'lib/types/tags';
import { useSignUpModal } from 'components/SignUpModal';

import { exploreSearchSummary } from './helpers';
import { FilterPostResult } from './FilterPostResult';

interface Props {
  title: string;
  richTitle: string | ReactElement;
  serverPosts: Post[] | null;
  serverCriteria: PostSearchCriteria;
  serverMeta: PostServiceMeta | null;
}

type LoadingState = 'idle' | 'needToFetch' | 'loading';

export const Explore = ({
  title, richTitle, serverPosts, serverCriteria, serverMeta,
}: Props) => {
  const API = useApi();
  const { isAuthed, currentUser } = useApplicationState();
  const { enqueueSnackbar } = useSnackbar();
  const router = useRouter();
  const teams = useTeams();
  const { showSignUpModal } = useSignUpModal();
  const [postsState, setPostsState] = useState<LoadingState>(serverPosts ? 'idle' : 'needToFetch');

  const [posts, setPosts] = useState<Post[] | null>(serverPosts);
  const [pagingMeta, setPagingMeta] = useState<ESPaginationInfo | undefined>(serverMeta?.paging);
  const [statsMeta, setStatsMeta] = useState<PostServiceStats | undefined>(serverMeta?.stats);
  const [criteria, setCriteria] = useState<PostSearchCriteria | undefined>(serverCriteria);
  const [filters, setFilters] = useState<(FilterResult | string)[]>([]);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [metaError, setMetaError] = useState<Error | undefined>(undefined);

  const [selectedPostIds, setSelectedPostIds] = useState<number[]>([]);
  const [drawer, setDrawer] = useState<DrawerType | undefined>();
  const [toCopyCredits, setToCopyCredits] = useState<Credit[]>([]);
  const [toCopyTags, setToCopyTags] = useState<Tag[]>([]);
  const [method, setMethod] = useState<Method | undefined>();
  const [similarPosts, setSimilarPosts] = useState<Post[]>([]);
  const [selectedSimilarPosts, setSelectedSimilarPosts] = useState<Post[]>([]);

  // After bulk tagging or bulk crediting, push the current route to force a reload of the posts
  const refreshView = (message?: string) => {
    router.reload();
    setDrawer(undefined);
    if (message) enqueueSnackbar(message, { variant: 'success' });
  };

  const changeMethod = (m?: Method) => {
    if (m !== 'copy') {
      setToCopyCredits([]);
      setToCopyTags([]);
    }
    setMethod(m);
  };

  useEffect(() => {
    async function fetchPosts() {
      setPostsState('loading');
      try {
        const { data, meta } = await API.getExplorePosts(criteria);
        setPagingMeta(meta.paging);
        setStatsMeta(meta.stats);
        setPosts([...(posts || []), ...data]);
        setPostsState('idle');
      } catch (err: any) {
        if (err?.name === 'AbortError' || err === 'CANCELLED') {
          return;
        }
        enqueueSnackbar(err?.message || 'There was an error fetching posts. Please double-check your filters.', { variant: 'error' });
        setError(err);
        setPostsState('idle');
        notify(err, 'features/Explore', {
          queryParams: router.query,
          criteria,
        });
      }
    }

    if (isAuthed && criteria) {
      if (postsState === 'needToFetch') {
        fetchPosts();
      }
    }
  }, [currentUser, criteria, postsState, isAuthed]);

  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    setError(undefined);
    setMetaError(undefined);
    setFilters(queryToFilters(router.query));
    const newCriteria = queryToCriteria(router.query);
    if (!isEqual(criteria, newCriteria)) {
      if (!isEqual(
        omit(criteria, 'from'),
        omit(newCriteria, 'from'),
      )) {
        // When criteria other than the `from` changes, we need to start fresh.
        setPosts([]);
      }

      setCriteria(newCriteria);
      setPostsState('needToFetch');
    }
  }, [router?.isReady, router?.query, criteria]);

  const exportCsv = async () => {
    try {
      await API.exportCSVReportForExplore(title, criteria);
      enqueueSnackbar('Your report is being generated. You will receive an email when it is ready.', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Your export failed. Please try again later.', { variant: 'error' });
    }
  };

  /**
   * Currently, only the MLB team (and admins) get an export button, and only when a list is used
   * as criteria.
  */
  const renderExportButton = () => {
    const MLB_TEAM_ID = 9;

    if (!(currentUser?.isAdmin || currentUser?.managedAccounts?.[0]?.teamId === MLB_TEAM_ID)
      || !criteria?.list) {
      return null;
    }

    return (
      <div className="pl-2 min-w-[70px]">
        <Button
          title="Generate Report"
          aria-label="Generate Report"
          onClick={() => exportCsv()}
        >
          CSV
          <Download fontSize="small" className="mr-1" />
        </Button>
      </div>
    );
  };

  const onIgnoreSuggestedCredit = (postId: number) => {
    setPosts((posts || [])?.filter((post) => post.id !== postId));
  };

  const sortOptions = (currentUser?.isAdmin && criteria?.imageSearch) || (criteria?.creditedStatus === 'suggested')
    ? { ...basicPostSortOptions, relevance: 'Relevance' }
    : basicPostSortOptions;

  const handleMyImportsClick = () => {
    const isCurrentlyOn = router.query.ownership === 'imported';

    if (currentUser?.id && !isCurrentlyOn) {
      trackClickSwitchToMyImports(API, currentUser.id);
    }

    if (isCurrentlyOn) {
      // Turn off: remove ownership=imported
      updateRouterQuery(router, { ownership: [] });
    } else {
      // Turn on: add ownership=imported
      updateRouterQuery(router, { ownership: 'imported' });
    }
  };

  const renderMyImportsButton = () => {
    if (!currentUser) {
      return null;
    }

    const isOn = router.query.ownership === 'imported';
    const buttonClassName = isOn
      ? 'btn-primary-xs uppercase whitespace-nowrap flex items-center'
      : 'btn-primary-outlined-xs uppercase whitespace-nowrap flex items-center';

    return (
      <button
        type="button"
        title=""
        aria-label="My Imports"
        onClick={handleMyImportsClick}
        className={buttonClassName}
      >
        {isOn ? (
          <CheckCircle fontSize="small" className="mr-1" />
        ) : (
          <ListAlt fontSize="small" className="mr-1" />
        )}
        <span className="ml-1">My Imports</span>
      </button>
    );
  };

  const handleBulkActionsClick = () => {
    const isOn = router.query.postView === 'list';

    if (isOn) {
      // Turn off: remove postView
      updateRouterQuery(router, { ...router.query, postView: [] });
    } else {
      // Turn on: set postView to list
      updateRouterQuery(router, { ...router.query, postView: 'list' });
    }
  };

  const renderBulkActionsButton = () => {
    const isOn = router.query.postView === 'list';
    const buttonClassName = isOn
      ? 'btn-primary-xs uppercase whitespace-nowrap flex items-center'
      : 'btn-primary-outlined-xs uppercase whitespace-nowrap flex items-center';

    return (
      <button
        type="button"
        title="Bulk Actions"
        aria-label="Bulk Actions"
        onClick={handleBulkActionsClick}
        className={buttonClassName}
      >
        {isOn ? (
          <CheckCircle fontSize="small" className="mr-1" />
        ) : (
          <ReorderIcon fontSize="small" className="mr-1" />
        )}
        Bulk Actions
      </button>
    );
  };

  const renderPostStats = () => {
    if (!statsMeta) {
      return null;
    }

    return (
      <div className="md:text-lg my-1 md:my-0">
        <PostStats
          stats={statsMeta}
          isLoading={postsState !== 'idle'}
          error={metaError}
        />
      </div>
    );
  };

  const renderDrawerContent = () => {
    if (drawer === 'credit') {
      return (
        <>
          <CreditDrawer
            postIds={selectedPostIds}
            credits={toCopyCredits}
            method={method}
            onCancel={() => setMethod(undefined)}
            onChange={refreshView}
          />
          <SelectedPosts
            title={`Credits will be applied to the following ${selectedPostIds.length} ${pluralize('post', selectedPostIds.length)}`}
            posts={posts?.filter((p) => selectedPostIds.includes(p.id)) || []}
            selectedPostIds={selectedPostIds}
            onChange={setSelectedPostIds}
          />
        </>
      );
    }

    return (
      <>
        <AddTags
          postIds={selectedPostIds}
          copyTags={toCopyTags}
          method={method}
          onChange={refreshView}
          onCancel={() => setMethod(undefined)}
        />
        <SelectedPosts
          title={`Tags will be applied to the following ${selectedPostIds.length} ${pluralize('post', selectedPostIds.length)}`}
          posts={posts?.filter((p) => selectedPostIds.includes(p.id)) || []}
          selectedPostIds={selectedPostIds}
          onChange={setSelectedPostIds}
        />
      </>
    );
  };

  const renderDrawer = () => {
    if (!drawer) {
      return null;
    }

    if (drawer === 'match') {
      return (
        <MatchDrawer
          count={selectedPostIds.length}
          drawer={drawer}
          onCancel={() => setDrawer(undefined)}
          posts={[
            ...posts?.filter((p) => selectedPostIds.includes(p.id)) || [],
            ...selectedSimilarPosts,
          ]}
          left={0}
        >
          <div className="px-6">
            <SelectedPosts
              title={`The following ${selectedPostIds.length} ${pluralize('post', selectedPostIds.length)} will be added to the match`}
              posts={[
                ...posts?.filter((p) => selectedPostIds.includes(p.id)) || [],
                ...selectedSimilarPosts,
              ]}
              selectedPostIds={selectedPostIds}
              onChange={setSelectedPostIds}
            />
            <SimilarPosts
              posts={similarPosts}
              onSelect={(post: Post) => {
                setSelectedPostIds([...selectedPostIds, post.id]);
                setSelectedSimilarPosts([...selectedSimilarPosts, post]);
              }}
            />
          </div>
        </MatchDrawer>
      );
    }

    return (
      <AddDrawer
        count={selectedPostIds.length}
        drawer={drawer}
        method={method}
        onChangeMethod={changeMethod}
        onCancel={() => setDrawer(undefined)}
        left={0}
      >
        <div className="px-6">
          {renderDrawerContent()}
        </div>
      </AddDrawer>
    );
  };

  const renderPostView = () => {
    const isCardView = (router.query.postView || 'card') === 'card';

    if (isCardView) {
      return (
        <FilterPostResult
          posts={posts || []}
          isLoading={postsState !== 'idle'}
          error={error}
          onIgnoreSuggestedCredit={onIgnoreSuggestedCredit}
        />
      );
    }

    return (
      <div className="w-full overflow-hidden">
        <Manager
          posts={posts || []}
          isLoading={postsState !== 'idle'}
          error={error}
          selectedPostIds={selectedPostIds}
          setSelectedPostIds={setSelectedPostIds}
          drawer={drawer}
          method={method}
          setDrawer={setDrawer}
          setSimilarPosts={setSimilarPosts}
          setToCopyTags={setToCopyTags}
          setToCopyCredits={setToCopyCredits}
        />
      </div>
    );
  };

  const renderPagination = () => {
    if (!pagingMeta) {
      return null;
    }

    return <Pagination paging={pagingMeta} isLoading={postsState !== 'idle'} />;
  };

  const handleFilterOverlayClick = () => {
    trackClickSignupEvent(API, 'explore-filters');
    showSignUpModal('Sign up to customize your search filters and explore posts!');
  };

  const isAnonymous = !!isAuthed && !currentUser;

  return (
    <div className="p-6 max-w-screen-2xl m-auto">
      <div className="relative grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
        <div className="col-span-2 sm:col-span-1 md:col-span-2 w-full">
          <span className="flex space-x-1">
            <OnlySavedFilter queryKey="onlySavedPosts" selected={!!criteria?.onlySavedPosts} disabled={isAnonymous} />
            <UserListFilter />
          </span>
        </div>
        <div className="col-span-2 sm:col-span-1 md:col-span-2 w-full">
          <MultiFilter
            filters={filters}
            useElasticSearch
            useNaturalLanguage
            allowImageSearch
            disabled={isAnonymous}
          />
        </div>
        <div className="col-span-1 sm:col-span-1">
          <NetworkFilter
            networks={criteria?.networks as string[]}
            includeSnapchat={userIsPaidTeamMember(currentUser, teams)}
            disabled={isAnonymous}
          />
        </div>
        <div className="col-span-1 sm:col-span-1">
          <FileTypeFilter
            fileTypes={criteria?.fileTypes as string[]}
            includeStories={userIsPaidTeamMember(currentUser, teams)}
            disabled={isAnonymous}
          />
        </div>
        <div className="col-span-1 md:col-span-1">
          <TimeframeOptionsFilter
            minPostedAt={criteria?.minPostedAt}
            maxPostedAt={criteria?.maxPostedAt}
            options={defaultTimeFrameOptions}
            disabled={isAnonymous}
          />
        </div>
        <div className="col-span-1 md:col-span-1">
          <MoreFilter
            engagement={{
              ...(criteria?.minViews && { minViews: criteria?.minViews }),
              ...(criteria?.maxViews && { maxViews: criteria?.maxViews }),
              ...(criteria?.minLikes && { minLikes: criteria?.minLikes }),
              ...(criteria?.maxLikes && { maxLikes: criteria?.maxLikes }),
              ...(criteria?.minShares && { minShares: criteria?.minShares }),
              ...(criteria?.maxShares && { maxShares: criteria?.maxShares }),
              ...(criteria?.minComments && { minComments: criteria?.minComments }),
              ...(criteria?.maxComments && { maxComments: criteria?.maxComments }),
            }}
            includeUncredited={criteria?.includeUncredited}
            roleTagSlugs={router.query.roleTags ? castArray(router.query.roleTags) : []}
            disabled={isAnonymous}
          />
        </div>
        <div className="col-span-1 md:col-span-1">
          <CreditedStatusFilter
            creditedStatus={criteria?.creditedStatus}
            showSuggestedCreditsFilter={!!currentUser}
            disabled={isAnonymous}
          />
        </div>

        <div className="col-span-2 md:col-span-3 lg:col-span-3 flex flex-wrap justify-end items-center gap-2">
          {renderMyImportsButton()}
          {renderBulkActionsButton()}
          {renderExportButton()}
          <SortFilter<PostSort>
            sortOptions={sortOptions}
            sort={criteria?.sort}
            defaultSort="postedAt"
            disabled={isAnonymous}
          />
          <ShareButton text={title} />
        </div>
        {isAnonymous && (
          <div
            className="absolute -top-2 left-0 right-0 bottom-0 bg-white bg-opacity-50 cursor-pointer z-10 flex items-center justify-center rounded-lg"
            onClick={handleFilterOverlayClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleFilterOverlayClick();
              }
            }}
            aria-label="Sign up to customize filters"
          >
            <div className="text-center p-4">
              <button
                type="button"
                className="bg-alpineTeal uppercase text-white px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  handleFilterOverlayClick();
                }}
              >
                Sign up to customize your search
              </button>
            </div>
          </div>
        )}
      </div>
      <div>
        <div className="flex flex-col md:flex-row md:justify-between md:items-center my-2">
          <div className="md:text-lg my-1 md:my-0">
            <FilterPostTitle
              title={richTitle || title}
              isLoading={postsState !== 'idle'}
            />
          </div>
          {renderPostStats()}
        </div>
      </div>
      {renderDrawer()}
      {renderPostView()}
      {renderPagination()}
      {exploreSearchSummary(posts || [], pagingMeta, statsMeta, title)}
    </div>
  );
};
